import { RawDataPoint, RegressionResult } from './types';
import { generateFitCurvePoints } from './generateFitCurve';
import { ChartSpec } from '../../../chart/chartSpec';

export interface ChartConfig {
  title: string;
  xAxisLabel: string;
  yAxisLabel: string;
  tooltipLabelX: string;
  tooltipLabelY: string;
  seriesNameRaw: string;
  seriesNameSelected: string;
  seriesNameFit: string;
  seriesColor: string;
  fitLineColor: string;
  xAxisReversed: boolean;
}

interface ChartOptionsParams {
  regressionResult: RegressionResult | null;
  rawDataTable: RawDataPoint[];
  selectedRowKeys: React.Key[];
  config: ChartConfig;
}

// Returns a declarative ChartSpec — the <Chart> wrapper applies theming, credits,
// exporting and renders it. (Previously returned a fully-built Highcharts.Options.)
export function getChartOptions({
  regressionResult,
  rawDataTable,
  selectedRowKeys,
  config
}: ChartOptionsParams): ChartSpec {
  const {
    title, xAxisLabel, yAxisLabel, tooltipLabelX, tooltipLabelY,
    seriesNameRaw, seriesNameSelected, seriesNameFit,
    seriesColor, fitLineColor, xAxisReversed
  } = config;

  if (!regressionResult) {
    return {
      title: 'Calculate regression to see results',
      series: []
    };
  }

  try {
    const { a_max_calc, b_exponent } = regressionResult;
    const indices = selectedRowKeys.map(key => Number(key));

    const allNormalizedPoints = rawDataTable
      .map(p => [p.so, p.kro])
      .filter(p => p[0] > 0 && p[1] > 0);

    const selectedNormalizedPoints = rawDataTable
      .filter(p => indices.includes(p.key))
      .map(p => [p.so, p.kro])
      .filter(p => p[0] > 0 && p[1] > 0);

    if (allNormalizedPoints.length === 0) {
      throw new Error('No valid points to plot.');
    }

    const sValues = allNormalizedPoints.map(p => p[0]);
    const minS = Math.min(...sValues);
    const maxS = Math.max(...sValues);

    const fitCurve = generateFitCurvePoints(minS, maxS, a_max_calc, b_exponent);

    return {
      title,
      xAxis: {
        type: 'logarithmic',
        reversed: xAxisReversed,
        title: xAxisLabel,
        labelsFormat: '{value:.2f}',
        min: 0.01,
        max: 1.0
      },
      yAxis: {
        type: 'logarithmic',
        title: yAxisLabel,
        labelsFormat: '{value:.3f}',
        min: 0.001,
        max: 1.0
      },
      tooltip: {
        headerFormat: '<b>{series.name}</b><br/>',
        pointFormat: `${tooltipLabelX}: {point.x:.3f}, ${tooltipLabelY}: {point.y:.3f}`
      },
      series: [
        {
          type: 'scatter',
          name: seriesNameRaw,
          data: allNormalizedPoints,
          color: 'gray',
          marker: { symbol: 'circle' }
        },
        {
          type: 'scatter',
          name: seriesNameSelected,
          data: selectedNormalizedPoints,
          color: seriesColor,
          marker: { symbol: 'diamond' }
        },
        {
          type: 'line',
          name: seriesNameFit,
          data: fitCurve,
          color: fitLineColor,
          marker: { enabled: false },
          dashStyle: 'Dot'
        }
      ] as ChartSpec['series']
    };
  } catch (e: any) {
    return {
      title: 'Error generating chart',
      series: [],
      // keep the legacy red error title
      options: { title: { style: { color: 'red' } } }
    };
  }
}
